import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API, trackEvent, QUIZ_URL } from "@/lib/api";

const imgSrc = (img) => (img && !img.startsWith("http") && !img.startsWith("/") ? `${API}/files/${img}` : img);

export default function Post() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/posts/${slug}`)
      .then(({ data }) => {
        setPost(data);
        document.title = `${data.title} — Wired 2 Win Golf`;
        trackEvent("post_view", { slug });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <main data-testid="post-not-found" className="pt-48 pb-32 max-w-3xl mx-auto px-6 text-center">
        <h1 className="font-display font-black text-5xl uppercase tracking-tighter">Out of bounds.</h1>
        <p className="text-white/50 mt-4">That article doesn't exist (anymore).</p>
        <Link to="/resources" className="inline-block mt-8 text-volt font-bold text-sm hover:text-white transition-colors">Back to Resources</Link>
      </main>
    );

  if (!post) return <main className="pt-48 pb-32 max-w-3xl mx-auto px-6 text-white/40 text-sm">Loading...</main>;

  return (
    <main data-testid="post-page" className="pt-32 lg:pt-40">
      <article className="max-w-3xl mx-auto px-6 pb-24">
        <Link to="/resources" data-testid="post-back-link" className="inline-flex items-center gap-2 text-white/50 hover:text-volt text-sm font-semibold transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" /> All resources
        </Link>
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-volt mb-4">
            {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </p>
          <h1 className="font-display font-black tracking-tighter leading-[0.95] text-4xl sm:text-6xl">{post.title}</h1>
        </Reveal>
        {post.image && (
          <Reveal delay={0.15}>
            <div className="mt-10 border border-white/10 p-2 bg-ink">
              <img src={imgSrc(post.image)} alt={post.title} className="w-full aspect-[16/9] object-cover" />
            </div>
          </Reveal>
        )}
        <Reveal delay={0.2}>
          <div className="mt-12 space-y-6" data-testid="post-content">
            {post.content.split("\n\n").map((para, i) => (
              <p key={i} className="text-white/70 text-base sm:text-lg leading-relaxed">{para}</p>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-16 border border-volt/40 bg-volt/5 p-8">
            <h2 className="font-display font-bold text-2xl tracking-tight mb-3">Want your full mental-game profile?</h2>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              The free Wired2Win assessment maps your Motivators, Strengths, Conditions and Stress in about six minutes.
            </p>
            <a
              href={QUIZ_URL}
              data-testid="post-quiz-cta"
              onClick={() => trackEvent("cta_click", { location: "post" })}
              className="group inline-flex items-center gap-2 bg-volt text-obsidian font-display font-bold px-6 py-3.5 hover:bg-white transition-colors"
            >
              Take the Assessment <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </Reveal>
      </article>
    </main>
  );
}
